import { useMemo, useState, type ReactNode } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useKpiQuotes, useLiveAdSpend } from "@/hooks/useKpiData";
import {
  QUOTE_CURRENCY,
  combineSpend,
  computeKpis,
  monthlyTrend,
  periodMonths,
  type KpiPeriod,
  type KpiSummary,
  type SourceRow,
  type SpendView,
  type TrendRow,
} from "@/lib/kpi-metrics";

const PERIODS: { value: KpiPeriod; label: string }[] = [
  { value: "this_month", label: "This month" },
  { value: "last_3_months", label: "Last 3 months" },
  { value: "last_12_months", label: "Last 12 months" },
];

const DASH = "—";

// Same formatter as the cards above (Dashboard.tsx), so "$" reads the same.
function money(value: number, decimals = 0): string {
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function count(n: number, singular: string, plural = `${singular}s`): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

function pct(value: number | null): string {
  return value === null ? DASH : `${Math.round(value * 100)}%`;
}

function platformMoney(value: number | null, currency: string | null): string {
  if (value === null) return DASH;
  return currency && currency !== QUOTE_CURRENCY ? `${money(value)} ${currency}` : money(value);
}

function spendHint(summary: KpiSummary, spend: SpendView, spendLoading: boolean, zeroHint: string): string {
  if (summary.spend === null) return spendLoading ? "Loading ad spend..." : spend.reason ?? "Ad spend unavailable";
  return zeroHint;
}

interface StatCardProps {
  title: string;
  value: string;
  hint: string;
}

function StatCard({ title, value, hint }: StatCardProps) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        <p className="text-xs text-muted-foreground mt-1">{hint}</p>
      </CardContent>
    </Card>
  );
}

interface KpiCardsProps {
  summary: KpiSummary;
  spend: SpendView;
  spendLoading: boolean;
}

function KpiCards({ summary, spend, spendLoading }: KpiCardsProps) {
  const spendText = summary.spend === null ? "" : money(summary.spend);
  const cacHint =
    summary.cac === null
      ? spendHint(summary, spend, spendLoading, "No new customers in this period")
      : `Ad spend ${spendText} ÷ ${count(summary.newCustomers, "new customer")}`;
  const cpaHint =
    summary.cpa === null
      ? spendHint(summary, spend, spendLoading, "No leads in this period")
      : `Ad spend ${spendText} ÷ ${count(summary.leads, "lead")}`;
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard title="Revenue" value={money(summary.revenue)} hint={`Won quotes, by quote date · ${count(summary.won, "quote")}`} />
      <StatCard title="Win rate" value={pct(summary.winRate)} hint={`${summary.won} of ${summary.decided} decided`} />
      <StatCard title="CAC" value={summary.cac === null ? DASH : money(summary.cac, 2)} hint={cacHint} />
      <StatCard title="CPA" value={summary.cpa === null ? DASH : money(summary.cpa, 2)} hint={cpaHint} />
    </div>
  );
}

function SourceTable({ rows }: { rows: SourceRow[] }) {
  if (rows.length === 0) return <p className="text-sm text-muted-foreground">No quotes in this period.</p>;
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Source</TableHead>
          <TableHead className="text-right">Leads</TableHead>
          <TableHead className="text-right">Won</TableHead>
          <TableHead className="text-right">Win rate</TableHead>
          <TableHead className="text-right">Revenue</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.source}>
            <TableCell>{r.source}</TableCell>
            <TableCell className="text-right">{r.leads}</TableCell>
            <TableCell className="text-right">{r.won}</TableCell>
            <TableCell className="text-right">{pct(r.winRate)}</TableCell>
            <TableCell className="text-right">{money(r.revenue)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function TrendTable({ rows, spend }: { rows: TrendRow[]; spend: SpendView }) {
  const heads = ["Leads", "Won", "Win rate", "Revenue", "Meta spend", "Google spend", "CAC", "CPA"];
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Month</TableHead>
            {heads.map((h) => (
              <TableHead key={h} className="text-right whitespace-nowrap">{h}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.month}>
              <TableCell className="whitespace-nowrap">{r.label}</TableCell>
              <TableCell className="text-right">{r.leads}</TableCell>
              <TableCell className="text-right">{r.won}</TableCell>
              <TableCell className="text-right">{pct(r.winRate)}</TableCell>
              <TableCell className="text-right">{money(r.revenue)}</TableCell>
              <TableCell className="text-right">{platformMoney(r.metaSpend, spend.metaCurrency)}</TableCell>
              <TableCell className="text-right">{platformMoney(r.googleSpend, spend.googleCurrency)}</TableCell>
              <TableCell className="text-right">{r.cac === null ? DASH : money(r.cac, 2)}</TableCell>
              <TableCell className="text-right">{r.cpa === null ? DASH : money(r.cpa, 2)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function SectionHeader({ period, onPeriod }: { period: KpiPeriod; onPeriod: (p: KpiPeriod) => void }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-2 mb-4">
      <div>
        <h3 className="font-display text-lg font-semibold text-foreground">Marketing KPIs</h3>
        <p className="text-xs text-muted-foreground">
          By quote date. Ad spend is live from Meta and Google. Excludes Square POS orders and invoices synced from
          Square.
        </p>
      </div>
      <Select value={period} onValueChange={(v) => onPeriod(v as KpiPeriod)}>
        <SelectTrigger className="w-40" aria-label="KPI period">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PERIODS.map((p) => (
            <SelectItem key={p.value} value={p.value}>
              {p.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function KpiSection() {
  const [period, setPeriod] = useState<KpiPeriod>("last_3_months");
  const quotesQ = useKpiQuotes();
  const spendQ = useLiveAdSpend();
  const quotes = quotesQ.data;
  const spendError = spendQ.error ? spendQ.error.message : null;

  const spend = useMemo(() => combineSpend(spendQ.data, spendError), [spendQ.data, spendError]);
  const summary = useMemo(
    () => (quotes ? computeKpis(quotes, spend.byMonth, periodMonths(period, new Date())) : null),
    [quotes, spend.byMonth, period],
  );
  const trend = useMemo(() => (quotes ? monthlyTrend(quotes, spend, new Date()) : []), [quotes, spend]);

  let body: ReactNode;
  if (quotesQ.error) {
    body = <p className="text-sm text-destructive">Couldn't load KPI data. {quotesQ.error.message}</p>;
  } else if (quotesQ.isLoading || !summary) {
    body = <div className="h-32 flex items-center justify-center text-muted-foreground">Loading...</div>;
  } else {
    body = (
      <div className="space-y-6">
        <KpiCards summary={summary} spend={spend} spendLoading={spendQ.isLoading} />
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Revenue by source</CardTitle>
          </CardHeader>
          <CardContent>
            <SourceTable rows={summary.bySource} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Last 12 months (by quote date)</CardTitle>
          </CardHeader>
          <CardContent>
            <TrendTable rows={trend} spend={spend} />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <section aria-label="Marketing KPIs">
      <SectionHeader period={period} onPeriod={setPeriod} />
      {body}
    </section>
  );
}
